import type {
  ChangeOrder,
  DeckingItem,
  GlobalCosts,
  ProposalBuilderData,
  ProposalNodeSet,
} from '../types';
import { hardwareGroupTotal } from './proposalHardwareCsvParser';
import type { PurchaseJobPlBucket } from '../types/purchasing';
import { isStructureClientSelected } from './proposalPublicGrandTotals';

export type ProposalBudgetLineKind = 'proposal' | 'change_order';

export type ProposalBudgetLineItem = {
  id: string;
  label: string;
  amount: number;
  kind: ProposalBudgetLineKind;
  matchKeys: string[];
};

export type RankedProposalBudgetLine = ProposalBudgetLineItem & { matched: boolean };

const GLOBAL_FREIGHT: ReadonlyArray<{
  key: keyof GlobalCosts;
  label: string;
  matchKeys: string[];
}> = [
  { key: 'globalFreightInbound', label: 'Freight — inbound', matchKeys: ['freight', 'inbound'] },
  { key: 'globalFreightOutbound', label: 'Freight — outbound', matchKeys: ['freight', 'outbound'] },
];

const GLOBAL_TRAVEL: ReadonlyArray<{
  key: keyof GlobalCosts;
  label: string;
  matchKeys: string[];
}> = [
  { key: 'baselineTravel', label: 'Baseline travel', matchKeys: ['travel', 'baseline'] },
  { key: 'additionalTravel', label: 'Additional travel', matchKeys: ['travel', 'additional'] },
];

function moneyish(value: unknown): number {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

/**
 * Timber is ordered through the timber RFQ, not job POs. Treat a label as
 * timber-only when it names timber/lumber and does not also name a
 * non-timber materials line (fasteners, hardware, pegs, decking, bases).
 */
export function isSeparateTimberOrderingLine(value: string): boolean {
  const text = value.toLowerCase();
  const timber = /\b(timbers?|lumber|woods?|oaks?|firs?|board\s?feet|bdft)\b/.test(text);
  if (!timber) return false;
  return !/\b(fasteners?|hardware|pegs?|decking|post\s?bases?|connectors?|hold-?downs?|screws?|nails?|bolts?)\b/.test(text);
}

function nonTimberMaterialsAmount(nodeSet: ProposalNodeSet): number {
  const materials = nodeSet.materials;
  return Math.max(0, moneyish(materials?.total) - moneyish(materials?.timberTotal)) * qtyOf(nodeSet);
}

function qtyOf(nodeSet: ProposalNodeSet): number {
  return nodeSet.quantity && nodeSet.quantity > 0 ? nodeSet.quantity : 1;
}

function enabledStructures(proposal: ProposalBuilderData): ProposalNodeSet[] {
  const phased = !!proposal.phasingEnabled && (proposal.phases?.length || 0) > 0;
  return (proposal.nodeSets || []).filter(ns =>
    isStructureClientSelected(ns, proposal.structureSelections, phased),
  );
}

function pushIf(lines: ProposalBudgetLineItem[], item: ProposalBudgetLineItem): void {
  if (Math.abs(item.amount) < 0.005) return;
  lines.push(item);
}

function lineLabel(structureName: string | undefined, multi: boolean, name: string, qty: number): string {
  const qtySuffix = qty > 1 ? ` ×${qty}` : '';
  const base = `${name}${qtySuffix}`;
  if (!multi || !structureName) return base;
  return `${structureName} · ${base}`;
}

function globalCostAmount(globalCosts: GlobalCosts | undefined, key: keyof GlobalCosts): number {
  if (!globalCosts) return 0;
  const item = globalCosts[key];
  if (!item || typeof item !== 'object' || Array.isArray(item)) return 0;
  if ('enabled' in item && item.enabled === false) return 0;
  if ('amount' in item) return moneyish(item.amount);
  return 0;
}

function pushGlobalLines(
  lines: ProposalBudgetLineItem[],
  globalCosts: GlobalCosts | undefined,
  items: ReadonlyArray<{ key: keyof GlobalCosts; label: string; matchKeys: string[] }>,
): void {
  items.forEach(item => {
    pushIf(lines, {
      id: `global-${item.key}`,
      label: item.label,
      amount: globalCostAmount(globalCosts, item.key),
      kind: 'proposal',
      matchKeys: item.matchKeys,
    });
  });
}

function deckingLines(
  nodeSet: ProposalNodeSet,
  multi: boolean,
): { lines: ProposalBudgetLineItem[]; total: number } {
  const qty = qtyOf(nodeSet);
  const materials = nodeSet.materials;
  const items = materials?.deckingItems || [];
  const lines: ProposalBudgetLineItem[] = [];
  if (items.length) {
    let total = 0;
    items.forEach((item: DeckingItem, index) => {
      const amount = moneyish(item.total) * qty;
      total += amount;
      pushIf(lines, {
        id: `${nodeSet.id}-decking-${item.id || index}`,
        label: lineLabel(nodeSet.name, multi, item.productName?.trim() || 'Decking', qty),
        amount,
        kind: 'proposal',
        matchKeys: ['decking', item.productName || ''],
      });
    });
    return { lines, total };
  }
  const total = moneyish(materials?.deckingTotal) * qty;
  pushIf(lines, {
    id: `${nodeSet.id}-decking`,
    label: lineLabel(nodeSet.name, multi, 'Decking', qty),
    amount: total,
    kind: 'proposal',
    matchKeys: ['decking'],
  });
  return { lines, total };
}

function materialsLines(nodeSet: ProposalNodeSet, multi: boolean): ProposalBudgetLineItem[] {
  const qty = qtyOf(nodeSet);
  const materials = nodeSet.materials;
  const lines: ProposalBudgetLineItem[] = [];
  const pegs = moneyish(materials?.pegsCost) * qty;
  const bases = moneyish(materials?.postBasesTotal) * qty;
  const fasteners = moneyish(materials?.fastenerPackageCost) * qty;
  const steel = hardwareGroupTotal(materials?.hardwareList, 'steel') * qty;
  const decking = deckingLines(nodeSet, multi);

  pushIf(lines, {
    id: `${nodeSet.id}-pegs`,
    label: lineLabel(nodeSet.name, multi, 'Pegs', qty),
    amount: pegs,
    kind: 'proposal',
    matchKeys: ['pegs', 'peg'],
  });
  pushIf(lines, {
    id: `${nodeSet.id}-post-bases`,
    label: lineLabel(nodeSet.name, multi, 'Post bases', qty),
    amount: bases,
    kind: 'proposal',
    matchKeys: ['post bases', 'post base', 'bases'],
  });
  pushIf(lines, {
    id: `${nodeSet.id}-fasteners`,
    label: lineLabel(nodeSet.name, multi, 'Fastener package', qty),
    amount: fasteners,
    kind: 'proposal',
    matchKeys: ['fastener', 'fasteners', 'hardware'],
  });
  pushIf(lines, {
    id: `${nodeSet.id}-steel`,
    label: lineLabel(nodeSet.name, multi, 'Steel', qty),
    amount: steel,
    kind: 'proposal',
    matchKeys: ['steel', 'hss', 'plate'],
  });
  lines.push(...decking.lines);

  const componentSum = pegs + bases + fasteners + steel + decking.total;
  const nonTimber = nonTimberMaterialsAmount(nodeSet);
  if (componentSum < 0.005 && nonTimber > 0.005) {
    pushIf(lines, {
      id: `${nodeSet.id}-materials`,
      label: lineLabel(nodeSet.name, multi, 'Materials', qty),
      amount: nonTimber,
      kind: 'proposal',
      matchKeys: ['materials', 'material'],
    });
  }
  return lines;
}

function structureTotalLine(
  nodeSet: ProposalNodeSet,
  multi: boolean,
  idSuffix: string,
  name: string,
  amount: number,
  matchKeys: string[],
): ProposalBudgetLineItem[] {
  const lines: ProposalBudgetLineItem[] = [];
  pushIf(lines, {
    id: `${nodeSet.id}-${idSuffix}`,
    label: lineLabel(nodeSet.name, multi, name, qtyOf(nodeSet)),
    amount,
    kind: 'proposal',
    matchKeys,
  });
  return lines;
}

function linesForStructure(
  nodeSet: ProposalNodeSet,
  bucket: PurchaseJobPlBucket,
  multi: boolean,
): ProposalBudgetLineItem[] {
  const qty = qtyOf(nodeSet);
  switch (bucket) {
    case 'timber':
      return materialsLines(nodeSet, multi);
    case 'design':
      return structureTotalLine(nodeSet, multi, 'design', 'Design', moneyish(nodeSet.design?.total) * qty, ['design']);
    case 'production':
      return structureTotalLine(
        nodeSet,
        multi,
        'production',
        'Production',
        moneyish(nodeSet.production?.total) * qty,
        ['production'],
      );
    case 'freight':
      return structureTotalLine(nodeSet, multi, 'freight', 'Freight', moneyish(nodeSet.freight?.total) * qty, ['freight']);
    case 'travel':
      return structureTotalLine(nodeSet, multi, 'travel', 'Travel', moneyish(nodeSet.travel?.total) * qty, ['travel']);
    case 'install': {
      const amount =
        (moneyish(nodeSet.install?.total) +
          moneyish(nodeSet.overhead?.total) +
          moneyish(nodeSet.install?.overheadTotal)) *
        qty;
      return structureTotalLine(nodeSet, multi, 'install', 'Install', amount, ['install']);
    }
    default:
      return [];
  }
}

function changeOrderRef(co: ChangeOrder): string {
  if (co.reference?.trim()) return co.reference.trim();
  const number = Number(co.number);
  if (Number.isFinite(number) && number > 0) {
    return `CO-${String(number).padStart(3, '0')}`;
  }
  return co.title?.trim() || 'Change order';
}

function changeOrderAmount(line: { costEstimate?: number; amount?: number }): number {
  if (typeof line.costEstimate === 'number') return line.costEstimate;
  return moneyish(line.amount);
}

function isApprovedChangeOrder(co: Pick<ChangeOrder, 'status'>): boolean {
  return co.status === 'approved';
}

function changeOrderBucketRollup(co: ChangeOrder, bucket: PurchaseJobPlBucket): number {
  if (co.budgetCostByBucket && Object.keys(co.budgetCostByBucket).length > 0) {
    return moneyish(co.budgetCostByBucket[bucket]);
  }
  if (co.pricingMode === 'line_item' && co.lineItems) {
    return co.lineItems.reduce((sum, item) => {
      if ((item.bucket || 'unassigned') !== bucket) return sum;
      return sum + changeOrderAmount(item);
    }, 0);
  }
  return 0;
}

function listChangeOrderLines(changeOrders: ChangeOrder[] | undefined, bucket: PurchaseJobPlBucket): ProposalBudgetLineItem[] {
  const lines: ProposalBudgetLineItem[] = [];
  (changeOrders || []).forEach(co => {
    if (!isApprovedChangeOrder(co)) return;
    const ref = changeOrderRef(co);

    if (co.pricingMode === 'line_item' && co.lineItems?.length) {
      co.lineItems.forEach(item => {
        if ((item.bucket || 'unassigned') !== bucket) return;
        if (bucket === 'timber' && isSeparateTimberOrderingLine(item.description || '')) return;
        pushIf(lines, {
          id: `co-${co.id || ref}-${item.id}`,
          label: `${ref} · ${item.description || 'Change order'}`,
          amount: changeOrderAmount(item),
          kind: 'change_order',
          matchKeys: [item.description || ''],
        });
      });
      return;
    }

    if (co.pricingMode === 'builder' && co.builderNodeSets?.length) {
      const sets = co.builderNodeSets.filter(ns => ns.enabled !== false);
      sets.forEach(ns => {
        const named = { ...ns, name: `${ref} · ${ns.name || 'Structure'}` };
        linesForStructure(named, bucket, true).forEach(line => {
          lines.push({ ...line, kind: 'change_order' });
        });
      });
      return;
    }

    const rollup = changeOrderBucketRollup(co, bucket);
    if (bucket === 'timber' && isSeparateTimberOrderingLine(co.title || '')) return;
    pushIf(lines, {
      id: `co-${co.id || ref}-${bucket}`,
      label: `${ref} · ${co.title || 'Change order'}`,
      amount: rollup,
      kind: 'change_order',
      matchKeys: [co.title || ''],
    });
  });
  return lines;
}

export function listProposalBucketLineItems(
  proposal: ProposalBuilderData | null | undefined,
  changeOrders: ChangeOrder[] | undefined,
  bucket: PurchaseJobPlBucket,
): ProposalBudgetLineItem[] {
  const lines: ProposalBudgetLineItem[] = [];
  if (proposal) {
    const structures = enabledStructures(proposal);
    const multi = structures.length > 1;
    structures.forEach(ns => {
      lines.push(...linesForStructure(ns, bucket, multi));
    });
    if (bucket === 'freight') pushGlobalLines(lines, proposal.globalCosts, GLOBAL_FREIGHT);
    if (bucket === 'travel') pushGlobalLines(lines, proposal.globalCosts, GLOBAL_TRAVEL);
  }
  lines.push(...listChangeOrderLines(changeOrders, bucket));
  return lines;
}

function changeOrderNonTimberMaterials(co: ChangeOrder): number {
  if (!isApprovedChangeOrder(co)) return 0;

  if (co.pricingMode === 'line_item' && co.lineItems?.length) {
    return co.lineItems.reduce((sum, item) => {
      if ((item.bucket || 'unassigned') !== 'timber') return sum;
      if (isSeparateTimberOrderingLine(item.description || '')) return sum;
      return sum + changeOrderAmount(item);
    }, 0);
  }

  if (co.pricingMode === 'builder' && co.builderNodeSets?.length) {
    return co.builderNodeSets
      .filter(ns => ns.enabled !== false)
      .reduce((sum, ns) => sum + nonTimberMaterialsAmount(ns), 0);
  }

  if (isSeparateTimberOrderingLine(co.title || '')) return 0;
  const rollup = moneyish(co.budgetCostByBucket?.timber);
  // Lump-sum credit against materials: pass the reduction through so the
  // envelope shrinks instead of silently clamping the credit to zero.
  if (rollup < 0) return rollup;
  const timberPart = (co.builderNodeSets || [])
    .filter(ns => ns.enabled !== false)
    .reduce((sum, ns) => sum + moneyish(ns.materials?.timberTotal) * qtyOf(ns), 0);
  return Math.max(0, rollup - timberPart);
}

/** Materials envelope for job POs — proposal materials minus timber takeoff. */
export function purchaseJobMaterialsBudget(
  proposal: ProposalBuilderData | null | undefined,
  changeOrders?: ChangeOrder[],
): number {
  let total = 0;
  if (proposal) {
    enabledStructures(proposal).forEach(ns => {
      total += nonTimberMaterialsAmount(ns);
    });
  }
  (changeOrders || []).forEach(co => {
    total += changeOrderNonTimberMaterials(co);
  });
  return total;
}

function normalizeToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function tokenStems(value: string): string[] {
  const normalized = normalizeToken(value);
  if (!normalized) return [];
  const words = normalized.split(/\s+/);
  const stems = words.map(word => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word));
  return Array.from(new Set([normalized, ...words, ...stems]));
}

export function findMatchingProposalLineId(
  scopeLabel: string,
  lines: ProposalBudgetLineItem[],
): string | null {
  const query = tokenStems(scopeLabel);
  if (!query.length) return null;
  let bestId: string | null = null;
  let bestScore = 0;
  for (const line of lines) {
    const hay = tokenStems([line.label, ...line.matchKeys].join(' '));
    const score = query.filter(token =>
      hay.some(item => item === token || item.includes(token) || token.includes(item)),
    ).length;
    if (score > 0 && score > bestScore) {
      bestId = line.id;
      bestScore = score;
    }
  }
  return bestId;
}

export function rankProposalBudgetLines(
  lines: ProposalBudgetLineItem[],
  scopeLabel?: string,
): RankedProposalBudgetLine[] {
  const matchId = findMatchingProposalLineId(scopeLabel || '', lines);
  const ranked = lines.map(line => ({ ...line, matched: line.id === matchId }));
  if (!matchId) return ranked;
  return [...ranked.filter(line => line.matched), ...ranked.filter(line => !line.matched)];
}

export function formatProposalBudgetUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
